import type { GetV5DevicesQueryParamsSortEnumKey } from '@emma/api_client/types';
import { useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useDevices } from '@/hooks/device';
import { paths } from '@/utility/global_paths';
import { deviceTableColumns } from '../common/table/column_definitions/Device';
import { deviceEmptyMessage } from '../common/table/deviceEmptyMessage';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { RegisterDeviceButton } from './RegisterDeviceButton';

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  name: 'nameLike',
  macAddress: 'macAddressLike',
  reportedIp: 'reportedIpLike',
  workspace: 'workspaceIdIn',
  isConnected: 'isConnected',
  fleet: 'fleetIdIn',
  workcenter: 'workcenterIdLike',
  os: 'osNameLike',
  createdAt: ['createdAfter', 'createdBefore'],
  reportedCoreVersion: 'reportedCoreVersionLike',
  state: 'stateIn',
};

const DeviceTableContent = () => {
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);
  const activeFilters = buildFilterParams(debouncedFilters, FILTERS);

  const queryParams = {
    ...serverTableParams<GetV5DevicesQueryParamsSortEnumKey>(pagination, sorting),
    ...activeFilters,
  };

  const { data, error, isLoading, mutate, isValidating } = useDevices(queryParams);
  const devices = useMemo(() => data?.devices ?? [], [data?.devices]);

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <Table
      addButton={(buttonProps) => (
        <RegisterDeviceButton {...buttonProps} onCreated={() => void mutate()} />
      )}
      columns={deviceTableColumns}
      data={devices}
      emptyMessage={deviceEmptyMessage(
        data?.itemCount,
        'No devices found',
        Object.keys(activeFilters).length > 0
      )}
      isLoading={Boolean((isLoading || error) && !data)}
      isValidating={isValidating}
      refresh={mutate}
      rowLink={({ id }) => paths.deviceById(id)}
      serverSide={{ pageCount, rowCount: totalItems }}
      title="Devices"
    />
  );
};

export const DeviceTable = () => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="Devices">
    <DeviceTableContent />
  </TableStoreProvider>
);
